import {useMemo} from 'react';

import {Box, Typography} from '@mui/material';
import {MuiOtpInput} from 'mui-one-time-password-input';
import {useTranslation} from 'react-i18next';

import {
  AppButton,
  AppButtonVariant,
  ButtonContainer,
} from '@/app/shared-components';
import Countdown from '@/app/shared-components/Countdown/Countdown';
import useWindowSize from '@/hooks/WindowSize/WindowSize';
import {COLORS} from '@/styles/colors';
import {isProduction} from '@/utils/environment';
import {isNumericCharacter} from '@/utils/isNumericCharacter';

import {AuthenticationWrapper} from '../../components/AuthenticationWrapper';

import {
  useVerifyViewModelProvider,
  VerifyViewModelProvider,
} from './ViewModelContext';

import './styles';

const VerifySmsContent = () => {
  const {t} = useTranslation();
  const {isMobile} = useWindowSize();
  const {
    smsCode,
    formattedPhone,
    isSignUp,
    showSendCode,
    maxLengthCode,
    role,
    isLoadingAmplifyAuth,
    handleOnChangeSmsCode,
    onCountdownStopped,
    handleSendCode,
    handleVerify,
    handleOnBack,
    getToken,
  } = useVerifyViewModelProvider();

  const isDisabledVerifyButton = useMemo(() => {
    return !smsCode || smsCode.length < maxLengthCode;
  }, [maxLengthCode, smsCode]);

  return (
    <AuthenticationWrapper
      title={t('verify_sms_page.title')}
      phone={formattedPhone}
      onBack={handleOnBack}
      showFooter={isMobile}
      showFdic={false}
      role={role}>
      <Box className="verify-sms-container">
        <section className="verify-sms-code-content">
          <Typography className="verify-sms-code-text">
            {t('verify_sms_page.sms_verification_code')}
          </Typography>

          <MuiOtpInput
            id="VerifySMSPage_smsCodeInput"
            validateChar={isNumericCharacter}
            className="MuiOtpInput-Box"
            autoFocus
            length={maxLengthCode}
            width="100%"
            value={smsCode}
            onChange={handleOnChangeSmsCode}
            style={{
              gap: 4,
              justifyContent: 'center',
              paddingInline: '16px',
            }}
            TextFieldsProps={{
              sx: {
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                },
              },
              variant: 'outlined',
              style: {
                width: '100%',
                textAlign: 'center',
                maxWidth: isMobile ? '100%' : '46px',
              },
              inputProps: {
                inputMode: 'numeric',
                pattern: '[0-9]*',
                type: 'text',
                style: {
                  textAlign: 'center',
                  fontSize: '16px',
                  height: '69px',
                  padding: 0,
                },
              },
            }}
          />

          {!showSendCode ? (
            <Countdown
              text={t('verify_sms_page.resend_code')}
              onStop={onCountdownStopped}
            />
          ) : (
            <AppButton
              id="VerifySMSPage_sendCodeButton"
              label={t('verify_sms_page.send_code')}
              onClick={handleSendCode}
              variant={AppButtonVariant.onText}
              disabled={isLoadingAmplifyAuth}
              labelColor={COLORS.ON_SURFACE_1}
            />
          )}
        </section>

        <section className="buttons-container">
          <ButtonContainer sticky>
            <AppButton
              id="VerifySMSPage_verifyButton"
              onClick={handleVerify}
              label={
                isSignUp
                  ? t('verify_sms_page.verify_and_sign_up_button')
                  : t('verify_sms_page.verify_button')
              }
              disabled={isDisabledVerifyButton}
              isLoading={isLoadingAmplifyAuth}
            />
          </ButtonContainer>

          {!isProduction && (
            <AppButton
              id="VerifySMSPage_getTokenButton"
              onClick={async () => await getToken()}
              label={t('dev_buttons.get_token_button')}
              variant={AppButtonVariant.devTool}
            />
          )}

          {/* Desktop only: reviews disclaimer kept next to the buttons (FDIC
              footer is hidden on desktop; mobile keeps the wrapper footer) */}
          {!isMobile && (
            <p className="verify-reviews-disclaimer">
              {t('authentication.footer.based_on')}
            </p>
          )}
        </section>
      </Box>
    </AuthenticationWrapper>
  );
};

const VerifySMSPage = () => (
  <VerifyViewModelProvider>
    <VerifySmsContent />
  </VerifyViewModelProvider>
);

export default VerifySMSPage;
